/**
 * Screen 4 — ranked date windows (spz-09-results-views).
 *
 * Auto-mounted at /windows by the pages-routing-convention in App.tsx.
 * Usage: /windows?trip=<uuid>
 * Standalone verification, no API, DB or login required:
 *   /windows?trip=x&fixture=1              organizer view, full fixture data
 *   /windows?trip=x&fixture=1&role=member  member view (no mutating controls)
 *   /windows?trip=x&fixture=empty          empty-state path
 *
 * Near-misses are negotiable facts: the section is never collapsed, never behind a
 * toggle, never inside a <details>.
 */
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import { AuthProvider, RequireAuth, useMembership } from '../lib/auth'
import { TripGateFallback, useResolvedTrip } from '../lib/useResolvedTrip'
import { FUNCTIONS_URL, isSupabaseConfigured, supabase } from '../lib/supabaseClient'
import { windowsCall } from '../lib/edgeRoutes'
import {
  SpzStyles,
  WindowCard,
  apiHeaders,
  formatMonthDay,
  formatRange,
  ordinal,
  parseISODate,
  type RankedWindow,
} from '../components/WindowCard'
import type { Availability, Household } from '../lib/types'

/**
 * A window that works for everyone except one household.
 *
 * TODO: import from #17 (`src/lib/windows/types.ts`, the `ranked-window` contract) once
 * it merges, and delete this stub. Shape is copied verbatim from that contract.
 */
export interface NearMiss {
  start: string
  end: string
  days: number
  missing_household_id: string
  blocking_days: string[]
}

interface WindowsResponse {
  windows: RankedWindow[]
  near_misses: NearMiss[]
}

/* --------------------------------------------------------------- plumbing -- */

function useQueryParams(): URLSearchParams {
  const [params, setParams] = useState(() => new URLSearchParams(globalThis.location?.search ?? ''))
  useEffect(() => {
    const onPop = () => setParams(new URLSearchParams(globalThis.location.search))
    globalThis.addEventListener('popstate', onPop)
    return () => globalThis.removeEventListener('popstate', onPop)
  }, [])
  return params
}

/* ------------------------------------------------------------- formatting -- */

/**
 * "on the 11th" / "on the 11th and 12th" / "on Jul 11–13".
 * Pure and exported so it can be eyeballed against the fixture.
 */
export function formatBlockingDays(days: string[]): string {
  const sorted = [...days].filter(Boolean).sort()
  const first = sorted[0]
  if (!first) return 'on those days'
  if (sorted.length === 1) return `on the ${ordinal(parseISODate(first).getUTCDate())}`
  const last = sorted[sorted.length - 1] as string
  if (sorted.length === 2) {
    const a = parseISODate(first)
    const b = parseISODate(last)
    const sameMonth =
      a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth()
    if (sameMonth) {
      return `on the ${ordinal(a.getUTCDate())} and ${ordinal(b.getUTCDate())}`
    }
    return `on ${formatMonthDay(first)} and ${formatMonthDay(last)}`
  }
  return `on ${formatRange(first, last)}`
}

/* --------------------------------------------------------------- fixtures -- */

const FIXTURE_LABELS: Record<string, string> = {
  'hh-carter': 'Carters',
  'hh-saltz': 'Saltzes',
  'hh-ruiz': 'Ruiz-Saltzes',
  'hh-okafor': 'Okafors',
}

const FIXTURE: WindowsResponse = {
  windows: [
    {
      booking_start: '2026-07-11',
      booking_end: '2026-07-18',
      core_start: '2026-07-13',
      core_end: '2026-07-16',
      full_attendance_days: 4,
      shoulder_days: 4,
      week_only_ok: true,
      rank: 1,
      absences: [
        { date: '2026-07-11', household_id: 'hh-carter', person_id: null },
        { date: '2026-07-11', household_id: 'hh-okafor', person_id: null },
        { date: '2026-07-12', household_id: 'hh-carter', person_id: null },
        { date: '2026-07-17', household_id: 'hh-ruiz', person_id: 'p-ruiz-1' },
        { date: '2026-07-18', household_id: 'hh-ruiz', person_id: null },
      ],
    },
    {
      booking_start: '2026-07-25',
      booking_end: '2026-08-01',
      core_start: '2026-07-27',
      core_end: '2026-07-29',
      full_attendance_days: 3,
      shoulder_days: 5,
      week_only_ok: true,
      rank: 2,
      absences: [
        { date: '2026-07-25', household_id: 'hh-saltz', person_id: null },
        { date: '2026-07-26', household_id: 'hh-saltz', person_id: 'p-saltz-2' },
        { date: '2026-07-30', household_id: 'hh-okafor', person_id: null },
        { date: '2026-07-31', household_id: 'hh-okafor', person_id: null },
        { date: '2026-08-01', household_id: 'hh-carter', person_id: null },
      ],
    },
    {
      booking_start: '2026-08-14',
      booking_end: '2026-08-18',
      core_start: '2026-08-15',
      core_end: '2026-08-16',
      full_attendance_days: 2,
      shoulder_days: 3,
      week_only_ok: false,
      rank: 3,
      absences: [
        { date: '2026-08-14', household_id: 'hh-ruiz', person_id: null },
        { date: '2026-08-17', household_id: 'hh-carter', person_id: 'p-carter-3' },
        { date: '2026-08-18', household_id: 'hh-carter', person_id: null },
        { date: '2026-08-18', household_id: 'hh-okafor', person_id: null },
      ],
    },
  ],
  near_misses: [
    {
      start: '2026-07-04',
      end: '2026-07-11',
      days: 8,
      missing_household_id: 'hh-carter',
      blocking_days: ['2026-07-11'],
    },
    {
      start: '2026-08-01',
      end: '2026-08-08',
      days: 8,
      missing_household_id: 'hh-okafor',
      blocking_days: ['2026-08-03', '2026-08-04'],
    },
  ],
}

const EMPTY: WindowsResponse = { windows: [], near_misses: [] }

/* ------------------------------------------------------------------- page -- */

const PAGE_CSS = `
.spz-nearmiss { display: flex; flex-direction: column; gap: 0.5rem; }
.spz-nearmiss ul { list-style: none; padding: 0; display: flex; flex-direction: column; gap: 0.45rem; }
.spz-nearmiss li {
  border-left: 3px solid var(--spz-line-strong);
  padding: 0.15rem 0 0.15rem 0.65rem;
  font-size: 0.95rem;
  line-height: 1.4;
}
.spz-nearmiss .spz-nm-why { color: var(--muted); font-size: 0.8rem; }
`

function WindowsScreen({
  params,
  tripId,
}: {
  params: URLSearchParams
  /** Resolved upstream — from `?trip=` or the user's membership. */
  tripId: string
}): JSX.Element {
  const fixtureParam = params.get('fixture')
  const fixtureRequested = fixtureParam !== null && fixtureParam !== '0'
  const fixtureEmpty = fixtureParam === 'empty'
  const fixtureRole = params.get('role') === 'member' ? 'member' : 'organizer'

  // Fixture mode never touches the network, so it never asks for a membership row.
  const { membership } = useMembership(fixtureRequested ? '' : tripId)

  const [data, setData] = useState<WindowsResponse | null>(null)
  const [labels, setLabels] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [recomputing, setRecomputing] = useState(false)
  const [usingFixture, setUsingFixture] = useState(false)
  const [pendingIntake, setPendingIntake] = useState<number | null>(null)

  // Organizer-only controls must not render for members. The server enforces this too;
  // this is the UI half.
  const isOrganizer =
    fixtureRequested || usingFixture
      ? fixtureRole === 'organizer'
      : membership?.role === 'organizer'

  const applyFixture = useCallback(() => {
    setData(fixtureEmpty ? EMPTY : FIXTURE)
    setLabels(FIXTURE_LABELS)
    setPendingIntake(fixtureEmpty ? 2 : null)
    setUsingFixture(true)
    setError(null)
  }, [fixtureEmpty])

  const load = useCallback(async () => {
    setLoading(true)
    if (fixtureRequested) {
      applyFixture()
      setLoading(false)
      return
    }
    if (!tripId) {
      // Only reachable in fixture mode without ?trip=; the signed-in path resolves
      // a trip upstream and never renders this screen without one.
      setError('No trip selected.')
      setLoading(false)
      return
    }
    if (!isSupabaseConfigured) {
      // No Supabase env in this build: fall back to the fixture so the screen is still
      // verifiable rather than white.
      applyFixture()
      setLoading(false)
      return
    }
    try {
      const headers = await apiHeaders()
      const [households, res] = await Promise.all([
        supabase.from('household').select('id, label').eq('trip_id', tripId),
        fetch(windowsCall(FUNCTIONS_URL, tripId).url, { headers }),
      ])
      const map: Record<string, string> = {}
      for (const h of (households.data ?? []) as Pick<Household, 'id' | 'label'>[]) {
        map[h.id] = h.label
      }
      setLabels(map)
      if (!res.ok) throw new Error(`Windows request failed (${res.status}).`)
      const body = (await res.json()) as WindowsResponse
      setData({ windows: body.windows ?? [], near_misses: body.near_misses ?? [] })
      setUsingFixture(false)
      setError(null)
      if ((body.windows ?? []).length === 0) {
        setPendingIntake(await countPendingIntake(tripId, Object.keys(map)))
      } else {
        setPendingIntake(null)
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        applyFixture()
      } else {
        setError(err instanceof Error ? err.message : 'Could not load windows.')
      }
    } finally {
      setLoading(false)
    }
  }, [applyFixture, fixtureRequested, tripId])

  useEffect(() => {
    void load()
  }, [load])

  const recompute = useCallback(async () => {
    if (usingFixture) return
    setRecomputing(true)
    setError(null)
    try {
      const res = await fetch(windowsCall(FUNCTIONS_URL, tripId).url, {
        method: 'POST',
        headers: await apiHeaders(),
      })
      if (res.status === 403) throw new Error('Only the organizer can recompute windows.')
      if (!res.ok) throw new Error(`Recompute failed (${res.status}).`)
      const body = (await res.json()) as WindowsResponse
      setData({ windows: body.windows ?? [], near_misses: body.near_misses ?? [] })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Recompute failed.')
    } finally {
      setRecomputing(false)
    }
  }, [tripId, usingFixture])

  const householdLabel = useCallback((id: string) => labels[id] ?? 'Unnamed household', [labels])

  const tripQuery = tripId ? `?trip=${encodeURIComponent(tripId)}` : ''
  const windows = data?.windows ?? []
  const nearMisses = data?.near_misses ?? []
  const intakeNote = useMemo(() => {
    if (pendingIntake === null || pendingIntake <= 0) return null
    return `${pendingIntake} household${pendingIntake === 1 ? " hasn't" : "s haven't"} completed intake — windows will change as availability comes in.`
  }, [pendingIntake])

  return (
    <main className="spz">
      <SpzStyles />
      <style href="spz-windows-page" precedence="spz">
        {PAGE_CSS}
      </style>

      <header className="spz-head">
        <h1>Date windows</h1>
        <p className="spz-sub">
          Ranked by full-attendance days. Everyone together for at least 2 consecutive days
          beats a longer window with most people.
        </p>
        <nav className="spz-nav">
          <a href={`/regions${tripQuery}${usingFixture ? (tripQuery ? '&' : '?') + 'fixture=1' : ''}`}>
            Regions &rarr;
          </a>
          {!usingFixture && <a href={`/intake${tripQuery}`}>Your household&rsquo;s answers</a>}
        </nav>
      </header>

      {usingFixture ? (
        <p className="spz-note">
          <strong>Fixture mode.</strong> Sample data, no API call. Viewing as{' '}
          {isOrganizer ? 'organizer' : 'member'}.
        </p>
      ) : null}

      {error ? <p className="spz-note">{error}</p> : null}

      {loading ? (
        <p className="spz-sub">Loading windows&hellip;</p>
      ) : windows.length === 0 ? (
        <div className="spz-empty">
          {intakeNote ? <p>{intakeNote}</p> : null}
          {isOrganizer ? (
            <>
              <p>
                No windows computed yet. Recompute to rank date windows from current
                availability.
              </p>
              <button
                type="button"
                className="spz-btn"
                onClick={() => void recompute()}
                disabled={recomputing || usingFixture}
              >
                {recomputing ? 'Recomputing…' : 'Recompute'}
              </button>
            </>
          ) : (
            <p>The organizer hasn&rsquo;t computed windows yet.</p>
          )}
        </div>
      ) : (
        <>
          {isOrganizer ? (
            <div className="spz-btn-row">
              <button
                type="button"
                className="spz-btn"
                onClick={() => void recompute()}
                disabled={recomputing || usingFixture}
              >
                {recomputing ? 'Recomputing…' : 'Recompute'}
              </button>
            </div>
          ) : null}
          <section className="spz-list">
            {windows.map((w) => (
              <WindowCard
                key={`${w.booking_start}-${w.booking_end}-${w.rank}`}
                window={w}
                householdLabel={householdLabel}
              />
            ))}
          </section>
        </>
      )}

      {nearMisses.length > 0 ? (
        <section className="spz-nearmiss">
          <h2 className="spz-section-title">Near misses</h2>
          <p className="spz-sub" style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
            Windows that work for everyone except one household. These are negotiable.
          </p>
          <ul>
            {nearMisses.map((nm) => (
              <li key={`${nm.start}-${nm.end}-${nm.missing_household_id}`}>
                <strong>{formatRange(nm.start, nm.end)}:</strong> all but the{' '}
                {householdLabel(nm.missing_household_id)}, who are out{' '}
                {formatBlockingDays(nm.blocking_days)}
                <span className="spz-nm-why"> &nbsp;({nm.days} days)</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </main>
  )
}

/**
 * Signed-in path: work out which trip this is before rendering the screen.
 *
 * Previously the screen demanded `?trip=<uuid>` and told anyone without one to
 * edit their own URL. Nothing in the app ever linked here with the parameter, so
 * in practice this page was unreachable.
 */
function WindowsResolved({ params }: { params: URLSearchParams }): JSX.Element {
  const resolution = useResolvedTrip()
  if (resolution.kind !== 'ready') {
    return <TripGateFallback resolution={resolution} what="date windows" />
  }
  return <WindowsScreen params={params} tripId={resolution.tripId} />
}

export default function WindowsPage(): JSX.Element {
  const params = useQueryParams()
  const fixtureParam = params.get('fixture')
  const fixtureRequested = fixtureParam !== null && fixtureParam !== '0'

  // Self-wrapped AuthProvider (nest-safe) so the page works before a root provider lands
  // in main.tsx. Fixture mode skips the login gate on purpose — it is how this screen is
  // verified with no API, no database and no session — and therefore skips trip
  // resolution too, which would otherwise find no membership and refuse to render.
  return (
    <AuthProvider>
      {fixtureRequested ? (
        <WindowsScreen params={params} tripId={params.get('trip') ?? ''} />
      ) : (
        <RequireAuth>
          <WindowsResolved params={params} />
        </RequireAuth>
      )}
    </AuthProvider>
  )
}

/**
 * Households with zero availability rows, so the empty state can say *why* there are no
 * windows. Best-effort: any read failure degrades to "unknown" and the generic message.
 */
async function countPendingIntake(
  tripId: string,
  householdIds: string[],
): Promise<number | null> {
  if (householdIds.length === 0) return null
  try {
    const { data, error } = await supabase
      .from('availability')
      .select('household_id')
      .eq('trip_id', tripId)
    if (error || !data) return null
    const seen = new Set((data as Pick<Availability, 'household_id'>[]).map((r) => r.household_id))
    return householdIds.filter((id) => !seen.has(id)).length
  } catch {
    return null
  }
}
